/**
 * Trusscore Product Table
 *
 * Complete catalog of Trusscore products with pricing (USD and CAD).
 * Products include Wall&CeilingBoard, SlatWall, and various trim types.
 *
 * Data can be loaded from the API at startup via loadProducts().
 * The hardcoded defaults below serve as a fallback if the API is unreachable.
 */

import { Product, ProductColor, ProductCustomField, CustomFieldColumnConfig, OutputLayoutConfig, CustomTableDef } from '../types';
import type { ActionButtonConfig } from '../config/actionButtons';
import { type CapturePolicy, DEFAULT_CAPTURE_POLICY } from '../config/capturePolicy';
import type { OutputSection } from '../config/outputSections';
import { fetchProductsPayload, fetchProductsVersion } from '../services/api';

/** Whether product data has been loaded from the API */
let _productsLoaded = false;
/** The retailer context the current productTable was loaded for. */
let _loadedRetailerId: number | null = null;
/** Server version token last seen for the loaded retailer (null = unknown). */
let _loadedVersion: string | null = null;
/** Custom-field column config for the loaded retailer context. */
let _customFieldConfig: CustomFieldColumnConfig[] = [];
/** Resolved output column layouts for the loaded retailer context. */
let _outputLayout: OutputLayoutConfig | null = null;
/** Custom output tables (scan sheets) for the loaded retailer context. */
let _customTables: CustomTableDef[] = [];
/** Configurable "Next Steps" action-button bar for the loaded retailer context. */
let _actionButtons: ActionButtonConfig[] = [];
/** Inline location map (below the BOM) for the loaded retailer context; null = none. */
let _inlineMapId: number | null = null;
/** Output footer sections for the loaded retailer context (resolved + ordered). */
let _outputSections: OutputSection[] = [];
/** Guest capture policy for the loaded retailer context. */
let _capturePolicy: CapturePolicy = DEFAULT_CAPTURE_POLICY;
/** Whether the loaded retailer's portal shows the accounts UI at all. */
let _accountsEnabled = true;

/**
 * Load product data from the API into the productTable array.
 * Falls back to hardcoded data if the API is unreachable.
 *
 * @param retailerId  - Retailer ID (default: 1 = Trusscore MSRP)
 * @param forceReload - Re-fetch even if already loaded (e.g. retailer changed)
 * @returns true if loaded from API, false if using fallback
 */
export async function loadProducts(retailerId: number = 1, forceReload = false): Promise<boolean> {
  // Already loaded for this retailer and not forcing — no-op.
  if (_productsLoaded && !forceReload && _loadedRetailerId === retailerId) return true;

  try {
    const {
      products, fieldConfig, outputLayout, customTables, actionButtons,
      capturePolicy, accountsEnabled, locationMaps, outputSections,
    } = await fetchProductsPayload(retailerId);
    if (products && products.length > 0) {
      // Replace the array contents with API data
      productTable.length = 0;
      productTable.push(...products);
      _customFieldConfig = fieldConfig ?? [];
      _outputLayout = outputLayout ?? null;
      _customTables = customTables ?? [];
      _actionButtons = actionButtons ?? [];
      _inlineMapId = locationMaps?.inlineMapId ?? null;
      _outputSections = outputSections ?? [];
      _capturePolicy = capturePolicy ?? DEFAULT_CAPTURE_POLICY;
      _accountsEnabled = accountsEnabled !== false;
      _productsLoaded = true;
      _loadedRetailerId = retailerId;
      console.log(`[ProductTable] Loaded ${products.length} products from API (retailer ${retailerId})`);
      return true;
    }
  } catch (err) {
    console.warn('[ProductTable] API unavailable, using fallback data:', err);
  }

  return false;
}

/**
 * Cheap freshness check, intended to run when a full estimate is displayed.
 *
 * When online, fetches the tiny version token for the loaded retailer and, if
 * it differs from what we last loaded, transparently rehydrates the product
 * data (forceReload). When offline or if the token endpoint is unreachable it
 * is a no-op — the currently loaded data stays in place (PWA-friendly). On the
 * very first call after boot we simply record the current server token without
 * a refetch, since boot already loaded fresh data.
 *
 * @param retailerId - retailer context (must match what was loaded at boot)
 * @returns true if a rehydrate occurred, false otherwise
 */
export async function maybeRefreshProducts(retailerId: number = 1): Promise<boolean> {
  // Never refresh on top of the fallback path — there's nothing loaded to compare.
  if (!_productsLoaded) return false;

  const serverVersion = await fetchProductsVersion(retailerId);
  if (serverVersion === null) return false; // offline / endpoint unavailable

  // Retailer changed out from under us, or first token we've seen since boot:
  // adopt the token without a redundant refetch (boot data is already current).
  if (_loadedRetailerId !== retailerId || _loadedVersion === null) {
    _loadedVersion = serverVersion;
    return false;
  }

  if (serverVersion === _loadedVersion) return false; // unchanged — no work

  const reloaded = await loadProducts(retailerId, true);
  if (reloaded) _loadedVersion = serverVersion;
  return reloaded;
}

/** Check whether products have been loaded from the API */
export function isProductsLoaded(): boolean {
  return _productsLoaded;
}

/** The retailer id the productTable is currently loaded for (null if fallback). */
export function getLoadedRetailerId(): number | null {
  return _loadedRetailerId;
}

/**
 * Custom-field column configuration for the loaded retailer context. Empty on
 * the fallback path (so the BOM/Placement Guide render only the built-in
 * Product # column — identical to legacy behavior).
 */
export function getCustomFieldConfig(): CustomFieldColumnConfig[] {
  return _customFieldConfig;
}

/**
 * Resolved output column layouts (BOM / placement) for the loaded retailer
 * context, or null on the fallback path / no custom layout (renderers then use
 * the built-in default column order — identical to legacy behavior).
 */
export function getOutputLayout(): OutputLayoutConfig | null {
  return _outputLayout;
}

/** Custom output tables (scan sheets) for the loaded retailer context. */
export function getCustomTables(): CustomTableDef[] {
  return _customTables;
}

/**
 * Configurable "Next Steps" action-button bar for the loaded retailer context.
 * Empty on the fallback path — the ActionButtons component then renders the
 * built-in DEFAULT_ACTION_BUTTONS so the bar is never empty.
 */
export function getActionButtons(): ActionButtonConfig[] {
  return _actionButtons;
}

/**
 * The location map configured to render inline below the BOM for the loaded retailer
 * context (universal → retailer placement, see /admin/maps), or null for none.
 */
export function getInlineMapId(): number | null {
  return _inlineMapId;
}

/**
 * Output footer sections for the loaded retailer context (universal defaults,
 * replaced / hidden per retailer, ordered, translated). Empty on the fallback path.
 */
export function getOutputSections(): OutputSection[] {
  return _outputSections;
}

/**
 * Guest capture policy for the loaded retailer context. Defaults to the full
 * (legacy) policy on the fallback path, so an unreachable API can never
 * accidentally drop the gate or relax which fields are asked for.
 */
export function getCapturePolicy(): CapturePolicy {
  return _capturePolicy;
}

/** Whether the loaded retailer's portal shows the accounts UI at all. */
export function getRetailerAccountsEnabled(): boolean {
  return _accountsEnabled;
}

/**
 * Resolved custom-field values for a product (parallel to findProductById).
 * Returns [] when the product or its fields aren't loaded.
 */
export function getCustomFieldsForProduct(productID: string): ProductCustomField[] {
  const product = productTable.find((p) => p.productID === productID);
  return product?.customFields ?? [];
}

// eslint-disable-next-line prefer-const
export let productTable: Product[] = [
  // ============================================================================
  // WALL & CEILING BOARD - WHITE
  // ============================================================================
  {
    productID: 'W&CB8White',
    acronym: 'W&CB',
    productNumber: 'TC1608',
    name: "Wall&CeilingBoard 8' White",
    lengthFeet: 8,
    sqft: 10.67,
    color: 'White',
    pricingUSD: 35.81,
    pricingCAD: 32.06,
  },
  {
    productID: 'W&CB10White',
    acronym: 'W&CB',
    productNumber: 'TC1610',
    name: "Wall&CeilingBoard 10' White",
    lengthFeet: 10,
    sqft: 13.33,
    color: 'White',
    pricingUSD: 44.64,
    pricingCAD: 40.07,
  },
  {
    productID: 'W&CB12White',
    acronym: 'W&CB',
    productNumber: 'TC1612',
    name: "Wall&CeilingBoard 12' White",
    lengthFeet: 12,
    sqft: 16.0,
    color: 'White',
    pricingUSD: 53.42,
    pricingCAD: 48.07,
  },
  {
    productID: 'W&CB14White',
    acronym: 'W&CB',
    productNumber: 'TC1614',
    name: "Wall&CeilingBoard 14' White",
    lengthFeet: 14,
    sqft: 18.67,
    color: 'White',
    pricingUSD: 62.3,
    pricingCAD: 56.12,
  },
  {
    productID: 'W&CB16White',
    acronym: 'W&CB',
    productNumber: 'TC1616',
    name: "Wall&CeilingBoard 16' White",
    lengthFeet: 16,
    sqft: 21.33,
    color: 'White',
    pricingUSD: 71.14,
    pricingCAD: 64.1,
  },
  {
    productID: 'W&CB20White',
    acronym: 'W&CB',
    productNumber: 'TC1620',
    name: "Wall&CeilingBoard 20' White",
    lengthFeet: 20,
    sqft: 26.67,
    color: 'White',
    pricingUSD: 89.51,
    pricingCAD: 80.13,
  },

  // ============================================================================
  // WALL & CEILING BOARD - GRAY
  // ============================================================================
  {
    productID: 'W&CB8Gray',
    acronym: 'W&CB',
    productNumber: 'GTC1608',
    name: "Wall&CeilingBoard 8' Gray",
    lengthFeet: 8,
    sqft: 10.67,
    color: 'Gray',
    pricingUSD: 36.29,
    pricingCAD: 37.74,
  },
  {
    productID: 'W&CB10Gray',
    acronym: 'W&CB',
    productNumber: 'GTC1610',
    name: "Wall&CeilingBoard 10' Gray",
    lengthFeet: 10,
    sqft: 13.33,
    color: 'Gray',
    pricingUSD: 45.72,
    pricingCAD: 47.18,
  },
  {
    productID: 'W&CB12Gray',
    acronym: 'W&CB',
    productNumber: 'GTC1612',
    name: "Wall&CeilingBoard 12' Gray",
    lengthFeet: 12,
    sqft: 16.0,
    color: 'Gray',
    pricingUSD: 54.86,
    pricingCAD: 56.62,
  },
  {
    productID: 'W&CB14Gray',
    acronym: 'W&CB',
    productNumber: 'GTC1614',
    name: "Wall&CeilingBoard 14' Gray",
    lengthFeet: 14,
    sqft: 18.67,
    color: 'Gray',
    pricingUSD: 66.11,
    pricingCAD: 66.05,
  },
  {
    productID: 'W&CB16Gray',
    acronym: 'W&CB',
    productNumber: 'GTC1616',
    name: "Wall&CeilingBoard 16' Gray",
    lengthFeet: 16,
    sqft: 21.33,
    color: 'Gray',
    pricingUSD: 77.4,
    pricingCAD: 75.44,
  },
  {
    productID: 'W&CB20Gray',
    acronym: 'W&CB',
    productNumber: 'GTC1620',
    name: "Wall&CeilingBoard 20' Gray",
    lengthFeet: 20,
    sqft: 26.67,
    color: 'Gray',
    pricingUSD: 95.48,
    pricingCAD: 94.31,
  },

  // ============================================================================
  // SLATWALL
  // ============================================================================
  {
    productID: 'SW8White',
    acronym: 'SW',
    productNumber: 'TL1841608',
    name: "SlatWall 8' White",
    lengthFeet: 8,
    sqft: 4, // 8' × 0.5' (6" height) = 4 sqft
    color: 'White',
    pricingUSD: 27.37,
    pricingCAD: 28.58,
  },
  {
    productID: 'SW8Gray',
    acronym: 'SW',
    productNumber: 'GTL1841608',
    name: "SlatWall 8' Gray",
    lengthFeet: 8,
    sqft: 4, // 8' × 0.5' (6" height) = 4 sqft
    color: 'Gray',
    pricingUSD: 32.01,
    pricingCAD: 33.04,
  },

  // ============================================================================
  // SNAP-IN TRIM J — works on both W&CB and SlatWall (replaces legacy 1/2" + 3/4" J-Trims)
  // ============================================================================
  {
    productID: 'JSnap10White',
    acronym: 'JSnap',
    productNumber: 'TCT12J10k',
    name: "Snap-In J Trim 10' White",
    lengthFeet: 10,
    sqft: 0,
    color: 'White',
    pricingUSD: 13.17,
    pricingCAD: 12.62,
  },
  {
    productID: 'JSnap10Gray',
    acronym: 'JSnap',
    productNumber: 'GTCT12J10k',
    name: "Snap-In J Trim 10' Gray",
    lengthFeet: 10,
    sqft: 0,
    color: 'Gray',
    pricingUSD: 14.38,
    pricingCAD: 13.77,
  },

  // ============================================================================
  // J-TRIM 1/2" — DEPRECATED, retained for backward compatibility with saved estimates
  // ============================================================================
  {
    productID: 'J1/210White',
    acronym: 'J1/2',
    productNumber: 'TCT12J10',
    name: 'J Trim 1/2" 10\' White',
    lengthFeet: 10,
    sqft: 0,
    color: 'White',
    pricingUSD: 11.97,
    pricingCAD: 11.47,
    active: false,
  },
  {
    productID: 'J1/210Gray',
    acronym: 'J1/2',
    productNumber: 'GTCT12J10',
    name: 'J Trim 1/2" 10\' Gray',
    lengthFeet: 10,
    sqft: 0,
    color: 'Gray',
    pricingUSD: 13.07,
    pricingCAD: 12.52,
    active: false,
  },

  // ============================================================================
  // J-TRIM 3/4" — DEPRECATED, retained for backward compatibility with saved estimates
  // ============================================================================
  {
    productID: 'J3/410White',
    acronym: 'J3/4',
    productNumber: 'RCT34J10',
    name: 'J Trim 3/4" 10\' White',
    lengthFeet: 10,
    sqft: 0,
    color: 'White',
    pricingUSD: 13.07,
    pricingCAD: 12.52,
    active: false,
  },
  {
    productID: 'J3/410Gray',
    acronym: 'J3/4',
    productNumber: 'GRCT34J10',
    name: 'J Trim 3/4" 10\' Gray',
    lengthFeet: 10,
    sqft: 0,
    color: 'Gray',
    pricingUSD: 15.29,
    pricingCAD: 14.65,
    active: false,
  },

  // ============================================================================
  // H-TRIM
  // ============================================================================
  {
    productID: 'H10White',
    acronym: 'H',
    productNumber: 'TLT58WTC10K',
    name: "Snap-In H Trim 10' White",
    lengthFeet: 10,
    sqft: 0,
    color: 'White',
    pricingUSD: 31.18,
    pricingCAD: 29.7,
  },
  {
    productID: 'H10Gray',
    acronym: 'H',
    productNumber: 'GTLT58HC10K',
    name: "Snap-In H Trim 10' Gray",
    lengthFeet: 10,
    sqft: 0,
    color: 'Gray',
    pricingUSD: 36.64,
    pricingCAD: 34.16,
  },

  // ============================================================================
  // BASE TRIM
  // ============================================================================
  {
    productID: 'Base10White',
    acronym: 'Base',
    productNumber: 'TCT12BT10',
    name: "Base Trim 10' White",
    lengthFeet: 10,
    sqft: 0,
    color: 'White',
    pricingUSD: 9.75,
    pricingCAD: 9.34,
  },
  {
    productID: 'Base10Gray',
    acronym: 'Base',
    productNumber: 'GTCT12BT10',
    name: "Base Trim 10' Gray",
    lengthFeet: 10,
    sqft: 0,
    color: 'Gray',
    pricingUSD: 10.75,
    pricingCAD: 10.3,
  },

  // ============================================================================
  // INSIDE COVE TRIM
  // ============================================================================
  {
    productID: 'Cove10White',
    acronym: 'Cove',
    productNumber: 'TCT12IC10',
    name: "Inside Cove Trim 10' White",
    lengthFeet: 10,
    sqft: 0,
    color: 'White',
    pricingUSD: 16.74,
    pricingCAD: 14.84,
  },
  {
    productID: 'Cove10Gray',
    acronym: 'Cove',
    productNumber: 'GTCT12IC10',
    name: "Inside Cove Trim 10' Gray",
    lengthFeet: 10,
    sqft: 0,
    color: 'Gray',
    pricingUSD: 19.14,
    pricingCAD: 16.44,
  },

  // ============================================================================
  // LARGE OUTSIDE CORNER
  // ============================================================================
  {
    productID: 'LOC10White',
    acronym: 'LOC',
    productNumber: 'TCT12LOC10',
    name: "Large Outside Corner 10' White",
    lengthFeet: 10,
    sqft: 0,
    color: 'White',
    pricingUSD: 20.76,
    pricingCAD: 19.89,
  },
  {
    productID: 'LOC10Gray',
    acronym: 'LOC',
    productNumber: 'GTCT12LOC10',
    name: "Large Outside Corner 10' Gray",
    lengthFeet: 10,
    sqft: 0,
    color: 'Gray',
    pricingUSD: 19.14,
    pricingCAD: 16.44,
  },

  // ============================================================================
  // SMALL OUTSIDE CORNER
  // ============================================================================
  {
    productID: 'SOC10White',
    acronym: 'SOC',
    productNumber: 'TCT12OC10',
    name: "Small Outside Corner 10' White",
    lengthFeet: 10,
    sqft: 0,
    color: 'White',
    pricingUSD: 20.23,
    pricingCAD: 19.39,
  },
  {
    productID: 'SOC10Gray',
    acronym: 'SOC',
    productNumber: 'GTCT12OC10',
    name: "Small Outside Corner 10' Gray",
    lengthFeet: 10,
    sqft: 0,
    color: 'Gray',
    pricingUSD: 22.13,
    pricingCAD: 21.21,
  },

  // ============================================================================
  // OUTSIDE CORNER ROUNDED
  // ============================================================================
  {
    productID: 'OCR10White',
    acronym: 'OCR',
    productNumber: 'TCT12IO10',
    name: "Outside Corner Rounded 10' White",
    lengthFeet: 10,
    sqft: 0,
    color: 'White',
    pricingUSD: 20.76,
    pricingCAD: 19.89,
  },
  {
    productID: 'OCR10Gray',
    acronym: 'OCR',
    productNumber: 'GTCT12IO10',
    name: "Outside Corner Rounded 10' Gray",
    lengthFeet: 10,
    sqft: 0,
    color: 'Gray',
    pricingUSD: 25.36,
    pricingCAD: 24.49,
  },

  // ============================================================================
  // SLATWALL TOP CAP
  // ============================================================================
  {
    productID: 'TC8White',
    acronym: 'TC',
    productNumber: 'TLT58J08',
    name: "SlatWall Top Cap 8' White",
    lengthFeet: 8,
    sqft: 0,
    color: 'White',
    pricingUSD: 14.32,
    pricingCAD: 15.51,
  },
  {
    productID: 'TC8Gray',
    acronym: 'TC',
    productNumber: 'GTLT58TC08',
    name: "SlatWall Top Cap 8' Gray",
    lengthFeet: 8,
    sqft: 0,
    color: 'Gray',
    pricingUSD: 14.32,
    pricingCAD: 15.51,
  },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Find a product by its product ID
 */
export function findProductById(productID: string): Product | undefined {
  return productTable.find((p) => p.productID === productID);
}

/**
 * Find a product by its product number
 */
export function findProductByNumber(productNumber: string): Product | undefined {
  return productTable.find((p) => p.productNumber === productNumber);
}

/**
 * Get all products of a specific type (by acronym)
 */
export function getProductsByType(acronym: string): Product[] {
  return productTable.filter((p) => p.acronym === acronym);
}

/**
 * Get all products of a specific color
 */
export function getProductsByColor(color: ProductColor): Product[] {
  return productTable.filter((p) => p.color === color);
}

/**
 * Get W&CB products by length and color
 */
export function getWCBProduct(lengthFeet: number, color: ProductColor): Product | undefined {
  return productTable.find(
    (p) => p.acronym === 'W&CB' && p.lengthFeet === lengthFeet && p.color === color
  );
}

/**
 * Get SlatWall product by color (always 8' length)
 */
export function getSlatWallProduct(color: ProductColor): Product | undefined {
  return productTable.find((p) => p.acronym === 'SW' && p.color === color);
}

/**
 * Get H-Trim product by color
 */
export function getHTrimProduct(color: ProductColor): Product | undefined {
  return productTable.find((p) => p.acronym === 'H' && p.color === color);
}

/**
 * Get Snap-In Trim J product by color. Used for all new-estimate J-trim calculations.
 */
export function getJTrimSnapInProduct(color: ProductColor): Product | undefined {
  return productTable.find(
    (p) => p.acronym === 'JSnap' && p.color === color && p.active !== false
  );
}

/**
 * @deprecated Use {@link getJTrimSnapInProduct} for new calculations. This helper is retained
 * only for resolving legacy J-Trim 1/2" SKUs on previously saved estimates.
 */
export function getJTrimHalfProduct(color: ProductColor): Product | undefined {
  return productTable.find((p) => p.acronym === 'J1/2' && p.color === color);
}

/**
 * @deprecated Use {@link getJTrimSnapInProduct} for new calculations. This helper is retained
 * only for resolving legacy J-Trim 3/4" SKUs on previously saved estimates.
 */
export function getJTrimThreeQuarterProduct(color: ProductColor): Product | undefined {
  return productTable.find((p) => p.acronym === 'J3/4' && p.color === color);
}

/**
 * Available W&CB panel lengths
 */
export const WCB_PANEL_LENGTHS = [8, 10, 12, 14, 16, 20] as const;
export type WCBPanelLength = (typeof WCB_PANEL_LENGTHS)[number];

export default productTable;
